import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import { BlogMarkdown } from "@/components/blog/BlogMarkdown";
import { getBlogPostBySlugBrowser, getBlogPostsSortedBrowser } from "@/lib/blog/posts-browser";

function formatListDate(iso: string) {
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

function formatPostDate(iso: string) {
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return iso;
  }
}

export function BlogIndexScreen() {
  const posts = getBlogPostsSortedBrowser();

  useEffect(() => {
    document.title = "Blog · crshm";
  }, []);

  return (
    <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-20 pt-8 sm:px-6 lg:px-8">
      <header className="mb-10">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-violet-600">Blog</p>
        <h1 className="mt-2 text-3xl font-black tracking-tight text-zinc-900 sm:text-4xl">Do or Dump desk notes</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-600">
          Plain-language posts on profit math, testing ideas, pricing, and what to do before you spend. Newest first —
          dates match when each piece was written for the funnel.
        </p>
      </header>

      <ul className="space-y-5">
        {posts.map((post) => (
          <li key={post.slug}>
            <article className="rounded-2xl border border-zinc-200/90 bg-white p-5 shadow-sm transition hover:border-violet-200 hover:shadow-md">
              <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{formatListDate(post.publishedAt)}</p>
              <h2 className="mt-1 text-lg font-bold leading-snug text-zinc-900">
                <Link to={`/blog/${post.slug}`} className="text-violet-800 underline-offset-2 hover:underline">
                  {post.title}
                </Link>
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{post.metaDescription}</p>
              <Link
                to={`/blog/${post.slug}`}
                className="mt-3 inline-block text-sm font-bold text-violet-700 underline-offset-2 hover:underline"
              >
                Read post →
              </Link>
            </article>
          </li>
        ))}
      </ul>

      <section className="mt-14 rounded-2xl border border-violet-200 bg-violet-50/90 px-5 py-6 shadow-sm">
        <h2 className="text-base font-black text-zinc-900">Want the interactive version?</h2>
        <p className="mt-2 text-sm leading-relaxed text-zinc-700">
          Run your numbers and get a verdict in the Do or Dump flow — same mindset as these posts, with your inputs.
        </p>
        <Link
          to="/do-or-dump"
          className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-violet-700"
        >
          Open Do or Dump
        </Link>
      </section>
    </main>
  );
}

export function BlogPostScreen() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlugBrowser(slug) : null;

  useEffect(() => {
    document.title = post ? `${post.title} · crshm` : "Blog · crshm";
  }, [post]);

  if (!post) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="text-sm text-zinc-600">This post could not be found.</p>
        <Link to="/blog" className="mt-4 inline-block text-sm font-bold text-violet-700 underline-offset-2 hover:underline">
          ← Back to blog
        </Link>
      </main>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishedAt,
    author: { "@type": "Organization", name: "crshm" },
    publisher: { "@type": "Organization", name: "crshm" },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <article className="mx-auto w-full max-w-3xl px-4 pb-20 pt-8 sm:px-6 lg:px-8">
        <nav className="mb-6 text-sm font-semibold">
          <Link to="/blog" className="text-violet-700 underline-offset-2 hover:underline">
            ← Blog
          </Link>
        </nav>

        <header className="border-b border-zinc-200 pb-8">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{formatPostDate(post.publishedAt)}</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-zinc-900 sm:text-4xl">{post.title}</h1>
          <p className="mt-4 text-sm leading-relaxed text-zinc-600">{post.metaDescription}</p>
        </header>

        <div className="pt-8">
          <BlogMarkdown markdown={post.body} />
        </div>

        <section className="mt-14 rounded-2xl border border-violet-200 bg-violet-50/90 px-5 py-6 shadow-sm">
          <h2 className="text-base font-black text-zinc-900">Run this on your own idea</h2>
          <p className="mt-2 text-sm leading-relaxed text-zinc-700">
            Do or Dump turns the same questions into a guided flow — verdict, numbers, and optional human add-ons.
          </p>
          <Link
            to="/do-or-dump"
            className="mt-4 inline-flex min-h-11 items-center rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-md transition hover:bg-violet-700"
          >
            Start Do or Dump
          </Link>
        </section>
      </article>
    </>
  );
}
